import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { RefreshCw, Eye, Trash2, Send, Globe, Link2, CheckCircle } from "lucide-react";

export default function AdminPSEOTab({ apiUrl, getAuthHeaders }) {
  const credFetch = (url, opts = {}) => fetch(url, { ...opts, credentials: "include" });

  const [pseoStats, setPseoStats] = useState(null);
  const [pseoPages, setPseoPages] = useState([]);
  const [pseoTemplates, setPseoTemplates] = useState([]);
  const [pseoLoading, setPseoLoading] = useState(false);
  const [pseoPageType, setPseoPageType] = useState("all");
  const [pseoStatus, setPseoStatus] = useState("all");
  const [gscData, setGscData] = useState(null);
  const [gscIndexing, setGscIndexing] = useState(null);
  const [gscLoading, setGscLoading] = useState(false);

  useEffect(() => {
    loadPseoStats();
    loadPseoPages();
    loadPseoTemplates();
    loadGscPerformance();
    loadGscIndexing();
  }, []);

  const loadPseoStats = async () => {
    try {
      const res = await credFetch(`${apiUrl}/api/pseo/dashboard/stats`);
      const data = await res.json();
      setPseoStats(data);
    } catch (error) { console.error("Failed to load PSEO stats"); }
  };

  const loadPseoPages = async () => {
    try {
      setPseoLoading(true);
      let url = `${apiUrl}/api/pseo/pages?limit=100`;
      if (pseoPageType !== "all") url += `&page_type=${pseoPageType}`;
      if (pseoStatus !== "all") url += `&status=${pseoStatus}`;
      const res = await credFetch(url);
      const data = await res.json();
      setPseoPages(data.pages || []);
    } catch (error) { console.error("Failed to load PSEO pages"); }
    finally { setPseoLoading(false); }
  };

  const loadPseoTemplates = async () => {
    try {
      const res = await credFetch(`${apiUrl}/api/pseo/templates`);
      const data = await res.json();
      setPseoTemplates(data.templates || []);
    } catch (error) { console.error("Failed to load templates"); }
  };

  const seedPseoTemplates = async () => {
    try {
      setPseoLoading(true);
      const res = await credFetch(`${apiUrl}/api/pseo/seed/templates`, { method: "POST", credentials: "include" });
      const data = await res.json();
      if (data.success) { toast.success("Templates created!"); loadPseoTemplates(); }
    } catch (error) { toast.error("Failed to seed templates"); }
    finally { setPseoLoading(false); }
  };

  const generateAllPseoPages = async () => {
    if (!window.confirm("Generate 200+ SEO pages for all tools, industries, locations?")) return;
    try {
      setPseoLoading(true);
      toast.info("Generating pages... This may take a minute.");
      const res = await credFetch(`${apiUrl}/api/pseo/seed/all-tools`, { method: "POST", credentials: "include" });
      const data = await res.json();
      if (data.success) { toast.success(`Created ${data.total_created} pages!`); loadPseoStats(); loadPseoPages(); }
    } catch (error) { toast.error("Generation failed"); }
    finally { setPseoLoading(false); }
  };

  const generateInternalLinks = async () => {
    try {
      setPseoLoading(true);
      const res = await credFetch(`${apiUrl}/api/pseo/links/generate-all`, { method: "POST", credentials: "include" });
      const data = await res.json();
      if (data.success) { toast.success(`Updated ${data.pages_updated} pages with internal links!`); loadPseoStats(); }
    } catch (error) { toast.error("Failed to generate links"); }
    finally { setPseoLoading(false); }
  };

  const deletePseoPage = async (slug) => {
    if (!window.confirm(`Delete page "${slug}"?`)) return;
    try {
      const res = await credFetch(`${apiUrl}/api/pseo/pages/${slug}`, { method: "DELETE" });
      const data = await res.json();
      if (data.success) { toast.success("Page deleted"); loadPseoPages(); loadPseoStats(); }
    } catch (error) { toast.error("Failed to delete"); }
  };

  const publishPseoPage = async (slug) => {
    try {
      const res = await credFetch(`${apiUrl}/api/pseo/pages/${slug}/publish`, { method: "POST", credentials: "include" });
      const data = await res.json();
      if (data.success) { toast.success("Page published"); loadPseoPages(); loadPseoStats(); }
    } catch (error) { toast.error("Failed to publish"); }
  };

  const loadGscPerformance = async () => {
    setGscLoading(true);
    try {
      const siteUrl = window.location.origin;
      const res = await credFetch(`${apiUrl}/api/gsc/performance?site_url=${encodeURIComponent(siteUrl)}&days=28`, { headers: getAuthHeaders(), credentials: "include" });
      const data = await res.json();
      if (data.success) setGscData(data.data);
    } catch (error) { console.error("GSC error:", error); }
    setGscLoading(false);
  };

  const loadGscIndexing = async () => {
    try {
      const siteUrl = window.location.origin;
      const res = await credFetch(`${apiUrl}/api/gsc/indexing-status?site_url=${encodeURIComponent(siteUrl)}`, { headers: getAuthHeaders(), credentials: "include" });
      const data = await res.json();
      if (data.success) setGscIndexing(data);
    } catch (error) { console.error("GSC indexing error:", error); }
  };

  const requestIndexing = async (urls) => {
    try {
      const res = await credFetch(`${apiUrl}/api/gsc/request-indexing`, { method: "POST", headers: { ...getAuthHeaders(), "Content-Type": "application/json" }, body: JSON.stringify({ urls }) });
      const data = await res.json();
      if (data.success) { toast.success(`${data.submitted} URLs submitted for indexing`); loadGscIndexing(); }
    } catch (error) { toast.error("Indexing request failed"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-pseo-tab">
      {pseoStats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-violet-500 to-purple-600 text-white"><CardContent className="p-5"><p className="text-violet-100 text-sm">Total Pages</p><p className="text-3xl font-bold mt-1">{pseoStats.total_pages || 0}</p></CardContent></Card>
          <Card className="bg-gradient-to-br from-green-500 to-emerald-600 text-white"><CardContent className="p-5"><p className="text-green-100 text-sm">Published</p><p className="text-3xl font-bold mt-1">{pseoStats.published || 0}</p></CardContent></Card>
          <Card className="bg-gradient-to-br from-blue-500 to-cyan-600 text-white"><CardContent className="p-5"><p className="text-blue-100 text-sm">In Sitemap</p><p className="text-3xl font-bold mt-1">{pseoStats.in_sitemap || 0}</p></CardContent></Card>
          <Card className="bg-gradient-to-br from-amber-500 to-orange-600 text-white"><CardContent className="p-5"><p className="text-amber-100 text-sm">Drafts</p><p className="text-3xl font-bold mt-1">{pseoStats.draft || 0}</p></CardContent></Card>
        </div>
      )}

      <Card>
        <CardHeader><CardTitle className="text-lg">Quick Actions</CardTitle></CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          <Button onClick={seedPseoTemplates} disabled={pseoLoading} variant="outline">Seed Templates</Button>
          <Button onClick={generateAllPseoPages} disabled={pseoLoading} className="bg-green-600 hover:bg-green-700">Generate All 200+ Pages</Button>
          <Button onClick={generateInternalLinks} disabled={pseoLoading} variant="outline">Generate Internal Links</Button>
          <Button onClick={() => { loadPseoStats(); loadPseoPages(); }} disabled={pseoLoading} variant="outline"><RefreshCw className={`w-4 h-4 mr-2 ${pseoLoading ? "animate-spin" : ""}`} /> Refresh</Button>
        </CardContent>
      </Card>

      {pseoStats?.by_type && (
        <Card>
          <CardHeader><CardTitle className="text-lg">Pages by Type</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(pseoStats.by_type).map(([type, count]) => (
                <div key={type} className="p-3 bg-slate-50 rounded-lg"><p className="text-xs text-slate-500 capitalize">{type}</p><p className="text-xl font-bold text-slate-800">{count}</p></div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">SEO Pages</CardTitle>
          <div className="flex gap-2">
            <select className="px-3 py-1 border rounded text-sm" value={pseoPageType} onChange={(e) => setPseoPageType(e.target.value)}>
              <option value="all">All Types</option>
              <option value="tool">Tool</option>
              <option value="usecase">Use Case</option>
              <option value="comparison">Comparison</option>
              <option value="industry">Industry</option>
              <option value="location">Location</option>
              <option value="glossary">Glossary</option>
              <option value="solution">Solution</option>
              <option value="best">Best</option>
            </select>
            <select className="px-3 py-1 border rounded text-sm" value={pseoStatus} onChange={(e) => setPseoStatus(e.target.value)}>
              <option value="all">All Status</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
            </select>
            <Button size="sm" onClick={loadPseoPages}>Filter</Button>
          </div>
        </CardHeader>
        <CardContent>
          {pseoLoading ? (
            <div className="text-center py-8"><RefreshCw className="w-6 h-6 animate-spin mx-auto text-violet-600" /></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Slug</th>
                    <th className="px-3 py-2 text-left font-medium">Type</th>
                    <th className="px-3 py-2 text-center font-medium">Quality</th>
                    <th className="px-3 py-2 text-center font-medium">Status</th>
                    <th className="px-3 py-2 text-center font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {pseoPages.slice(0, 50).map((page, idx) => (
                    <tr key={idx} className="hover:bg-slate-50">
                      <td className="px-3 py-2"><code className="text-xs bg-slate-100 px-1 rounded">/{page.slug}</code></td>
                      <td className="px-3 py-2 capitalize">{page.page_type}</td>
                      <td className="px-3 py-2 text-center">
                        <span className={`px-2 py-0.5 rounded text-xs ${page.quality_score >= 80 ? "bg-green-100 text-green-700" : page.quality_score >= 50 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>{page.quality_score || 0}</span>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span className={`px-2 py-0.5 rounded text-xs ${page.status === "published" ? "bg-green-100 text-green-700" : "bg-slate-100"}`}>{page.status}</span>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <div className="flex justify-center gap-1">
                          <Button size="sm" variant="ghost" onClick={() => window.open(`/${page.slug}`, "_blank")}><Eye className="w-3 h-3" /></Button>
                          {page.status === "draft" && <Button size="sm" variant="ghost" className="text-green-600" onClick={() => publishPseoPage(page.slug)}><CheckCircle className="w-3 h-3" /></Button>}
                          <Button size="sm" variant="ghost" className="text-red-600" onClick={() => deletePseoPage(page.slug)}><Trash2 className="w-3 h-3" /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {pseoPages.length === 0 && <div className="text-center py-8 text-slate-500">No pages found. Click "Generate All 200+ Pages" to create SEO pages.</div>}
              {pseoPages.length > 50 && <p className="text-center text-sm text-slate-500 py-2">Showing 50 of {pseoPages.length} pages</p>}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-lg">Sitemaps</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {["tool", "usecase", "comparison", "industry", "location", "glossary", "solution", "best"].map((type) => (
              <a key={type} href={`${apiUrl}/api/pseo/sitemap/${type}.xml`} target="_blank" rel="noopener noreferrer" className="p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                <p className="text-xs text-slate-500 capitalize">{type}</p>
                <p className="text-sm font-medium text-violet-600">sitemap-{type}.xml</p>
              </a>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="border-2 border-blue-200">
        <CardHeader className="bg-gradient-to-r from-blue-50 to-cyan-50">
          <CardTitle className="text-lg flex items-center justify-between">
            <span className="flex items-center gap-2"><Globe className="w-5 h-5 text-blue-600" />Google Search Console{gscData?.is_sample_data && <Badge variant="outline" className="text-xs">Sample Data</Badge>}</span>
            {gscData?.is_sample_data && (
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={async () => {
                try {
                  const redirectUri = window.location.origin + "/admin";
                  const res = await credFetch(`${apiUrl}/api/gsc/oauth/login?redirect_uri=${encodeURIComponent(redirectUri)}`);
                  const data = await res.json();
                  if (data.oauth_url) window.location.href = data.oauth_url;
                } catch (e) { toast.error("Failed to start GSC connection"); }
              }}><Link2 className="w-4 h-4 mr-1" /> Connect GSC</Button>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6 pt-4">
          {gscData && (
            <div>
              <h4 className="font-medium text-sm text-slate-600 mb-3">Last 28 Days Performance</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-blue-50 rounded-lg"><p className="text-xs text-blue-600">Clicks</p><p className="text-2xl font-bold text-blue-700">{gscData.totals?.clicks?.toLocaleString()}</p></div>
                <div className="p-4 bg-purple-50 rounded-lg"><p className="text-xs text-purple-600">Impressions</p><p className="text-2xl font-bold text-purple-700">{gscData.totals?.impressions?.toLocaleString()}</p></div>
                <div className="p-4 bg-green-50 rounded-lg"><p className="text-xs text-green-600">CTR</p><p className="text-2xl font-bold text-green-700">{gscData.totals?.ctr}%</p></div>
                <div className="p-4 bg-amber-50 rounded-lg"><p className="text-xs text-amber-600">Avg Position</p><p className="text-2xl font-bold text-amber-700">{gscData.totals?.position?.toFixed(1)}</p></div>
              </div>
            </div>
          )}
          {gscData?.top_queries && (
            <div>
              <h4 className="font-medium text-sm text-slate-600 mb-3">Top Search Queries</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50"><tr><th className="px-3 py-2 text-left">Query</th><th className="px-3 py-2 text-right">Clicks</th><th className="px-3 py-2 text-right">Impressions</th><th className="px-3 py-2 text-right">CTR</th><th className="px-3 py-2 text-right">Position</th></tr></thead>
                  <tbody className="divide-y">
                    {gscData.top_queries.map((q, i) => (
                      <tr key={i} className="hover:bg-slate-50">
                        <td className="px-3 py-2 font-medium">{q.query}</td>
                        <td className="px-3 py-2 text-right">{q.clicks.toLocaleString()}</td>
                        <td className="px-3 py-2 text-right">{q.impressions.toLocaleString()}</td>
                        <td className="px-3 py-2 text-right">{q.ctr}%</td>
                        <td className="px-3 py-2 text-right">{q.position.toFixed(1)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {gscIndexing && (
            <div>
              <h4 className="font-medium text-sm text-slate-600 mb-3">Indexing Status</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div className="p-3 bg-green-50 rounded-lg text-center"><p className="text-xs text-green-600">Indexed</p><p className="text-xl font-bold text-green-700">{gscIndexing.status_breakdown?.indexed}</p></div>
                <div className="p-3 bg-amber-50 rounded-lg text-center"><p className="text-xs text-amber-600">Pending</p><p className="text-xl font-bold text-amber-700">{gscIndexing.status_breakdown?.pending}</p></div>
                <div className="p-3 bg-red-50 rounded-lg text-center"><p className="text-xs text-red-600">Errors</p><p className="text-xl font-bold text-red-700">{gscIndexing.status_breakdown?.errors}</p></div>
                <div className="p-3 bg-slate-50 rounded-lg text-center"><p className="text-xs text-slate-600">Total Pages</p><p className="text-xl font-bold text-slate-700">{gscIndexing.total_pages}</p></div>
              </div>
              <Button onClick={() => {
                const pendingUrls = gscIndexing.pages?.filter((p) => p.status === "pending").map((p) => window.location.origin + p.url).slice(0, 10);
                if (pendingUrls?.length > 0) requestIndexing(pendingUrls);
                else toast.info("No pending URLs to index");
              }} className="bg-blue-600 hover:bg-blue-700" size="sm"><Send className="w-4 h-4 mr-2" /> Request Indexing for Pending URLs</Button>
            </div>
          )}
          {gscLoading && (
            <div className="text-center py-8"><RefreshCw className="w-6 h-6 animate-spin mx-auto text-blue-600" /><p className="text-sm text-slate-500 mt-2">Loading Search Console data...</p></div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
